import { useAutoApplyDifferentSearchMode } from '#pplx/frontend/apps/ask/ask-input/ask-input-attributions/hooks/useAutoApplyDifferentSearchMode.js';
import { SEARCH_MODES } from '#pplx/frontend/apps/ask/ask-input/ask-input-attributions/mode-toggle-constants.js';
import { useThreadConfig } from '#pplx/frontend/apps/ask/providers/ThreadSettingsProviderShared.js';
import { getSearchModeLabel, SearchModeStrings, } from '#pplx/frontend/apps/ask/search-modes/core/search-modes-names.js';
import { getSearchModeIcon, SearchMode } from '#pplx/frontend/ask-models/types/search-modes.js';
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { useMemo } from 'react';
import { defineMessage, useIntl } from 'react-intl';
const ASK_INPUT_CONTAINER_SELECTOR = '[data-ask-input-container]';
const COMPUTER_OFF_TOOLTIP_SUBTITLE = defineMessage({
    defaultMessage: 'For work and more complex tasks',
    id: 'a6f0fe45f3',
    description: 'Secondary line for the Computer mode hover tooltip in the ask input',
});
const SEARCH_OFF_TOOLTIP_SUBTITLE = defineMessage({
    defaultMessage: 'For fast answers',
    id: 'fd32f467fe',
    description: 'Secondary line for the Search mode hover tooltip in the ask input',
});
/**
 * Ask-domain adapter over the generic `ModeToggleGroup` primitive. Translates
 * `SearchMode` policy — visibility, mid-thread locking, the displayed
 * label/icon for the active submode, and the contextual auto-apply side effect
 * — into the shared group's declarative props.
 */
export function useAskInputModeToggleGroupProps({ searchMode, selectedSearchMode, onSearchModeSelect, isFollowUp, omittedSearchModes = EMPTY_ARRAY, toggleVisibility = 'all', renderMenu, isSearchModeDisabled, matchMenuToAskInputWidth = false, askInputLayoutContainerRef, overlayKind = 'menu', menuOpen, onMenuOpenChange, labelVisibility = 'active', appearance, renderComputerModeToggle, autoApplyEnabled = true, }) {
    const { $t } = useIntl();
    const { hideSearchModeToggle } = useThreadConfig();
    const isComputerDisabledByOrg = isSearchModeDisabled?.(SearchMode.ASI) ?? false;
    const allowSearchToComputerBranching = !omittedSearchModes.includes(SearchMode.ASI);
    // Contextual mode auto-apply is a domain side effect and must not live in a
    // UI primitive — it stays here in the adapter.
    useAutoApplyDifferentSearchMode({
        enabled: autoApplyEnabled,
        selectedSearchMode,
        isFollowUp,
        onSearchModeSelect,
        omittedSearchModes,
    });
    const isSearchActive = SEARCH_MODES.includes(searchMode);
    const isComputerActive = searchMode === SearchMode.ASI;
    // Mid-thread, ASI/BROWSER_AGENT can't be toggled off. Computer keeps its
    // dropdown openable; Search (browser control) and tier locks do not.
    const searchMidThreadLocked = isFollowUp && searchMode === SearchMode.BROWSER_AGENT;
    const computerMidThreadLocked = isFollowUp && isComputerActive && !allowSearchToComputerBranching;
    // Visibility rules: hide the toggle that can't be reached from the current
    // follow-up state, and honor `active-only` by hiding an inactive Search.
    const showSearchRaw = isSearchActive ||
        !(isFollowUp && selectedSearchMode === SearchMode.ASI && !allowSearchToComputerBranching);
    const showSearchToggle = !hideSearchModeToggle && showSearchRaw && (toggleVisibility === 'all' || isSearchActive);
    const showComputerToggle = !hideSearchModeToggle &&
        (isComputerActive ||
            !(isFollowUp &&
                (selectedSearchMode === SearchMode.BROWSER_AGENT ||
                    (isSearchActive && !allowSearchToComputerBranching))));
    // Displayed icon/label follow the active submode and fall back to the
    // toggle's default when off.
    const searchDisplayMode = isSearchActive ? searchMode : SearchMode.SEARCH;
    const searchLockedReason = searchMidThreadLocked
        ? $t({
            defaultMessage: 'Start a new session to turn off browser control',
            id: '345e2f3549',
            description: 'Tooltip when browser-agent mode is locked mid-thread',
        })
        : undefined;
    const computerLockedReason = computerMidThreadLocked
        ? $t({
            defaultMessage: 'Start a new session to turn off computer',
            id: 'c7e94bbb1e',
            description: 'Tooltip when computer mode is locked mid-thread',
        })
        : isComputerDisabledByOrg
            ? $t({
                defaultMessage: 'Disabled by your organization',
                id: 'e46057f35d',
                description: 'Message shown when a search mode is disabled by organization policy',
            })
            : undefined;
    const items = useMemo(() => [
        {
            value: SearchMode.SEARCH,
            activeValues: SEARCH_MODES,
            label: $t(getSearchModeLabel(searchDisplayMode)),
            icon: getSearchModeIcon(searchDisplayMode),
            offTooltip: $t({
                defaultMessage: 'Use {label}',
                id: 'c7310dd1cb',
                description: 'Hover tooltip on the search mode chip when it is off',
            }, { label: $t(getSearchModeLabel(SearchMode.SEARCH)) }),
            offTooltipSubtitle: $t(SEARCH_OFF_TOOLTIP_SUBTITLE),
            lockedReason: searchLockedReason,
            hidden: !showSearchToggle,
            renderMenu,
        },
        {
            value: SearchMode.ASI,
            label: $t(getSearchModeLabel(SearchMode.ASI)),
            icon: getSearchModeIcon(SearchMode.ASI),
            offTooltip: $t(SearchModeStrings[SearchMode.ASI].suggestionTooltip),
            offTooltipSubtitle: $t(COMPUTER_OFF_TOOLTIP_SUBTITLE),
            lockedReason: computerLockedReason,
            // Computer mode can't be turned off mid-thread but still opens its
            // menu. The org-policy lock is a full lock: opening the menu would
            // swallow the pointer events that surface the disabled tooltip.
            allowMenuWhenLocked: computerMidThreadLocked,
            hidden: !showComputerToggle,
            renderMenu,
            renderToggle: renderComputerModeToggle
                ? (toggle) => renderComputerModeToggle({
                    toggle,
                    onSelectComputer: () => onSearchModeSelect(SearchMode.ASI),
                    searchMode,
                })
                : undefined,
        },
    ], [
        $t,
        computerLockedReason,
        computerMidThreadLocked,
        onSearchModeSelect,
        renderMenu,
        renderComputerModeToggle,
        searchMode,
        searchDisplayMode,
        searchLockedReason,
        showComputerToggle,
        showSearchToggle,
    ]);
    return useMemo(() => ({
        items,
        value: searchMode,
        onValueSelect: onSearchModeSelect,
        labelVisibility,
        appearance,
        overlayKind,
        menuOpen,
        onMenuOpenChange,
        matchMenuToContainerWidth: matchMenuToAskInputWidth,
        layoutContainerRef: askInputLayoutContainerRef,
        containerSelector: ASK_INPUT_CONTAINER_SELECTOR,
        testIdPrefix: 'ask-input-mode-toggle',
    }), [
        askInputLayoutContainerRef,
        items,
        labelVisibility,
        appearance,
        matchMenuToAskInputWidth,
        menuOpen,
        onMenuOpenChange,
        onSearchModeSelect,
        overlayKind,
        searchMode,
    ]);
}
//# sourceMappingURL=useAskInputModeToggleGroupProps.js.map